import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const usePdf = (fileTitle) => {
  const targetRef = useRef();
  const [loading, setLoading] = useState(false);

  const waitForImages = async (element) => {
    const images = element.querySelectorAll("img");

    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();

        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      }),
    );
  };

  const generatePDF = async () => {
    if (!targetRef.current) return;

    setLoading(true);

    try {
      await waitForImages(targetRef.current);

      const canvas = await html2canvas(targetRef.current, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = 210;
      const pageHeight = 297;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      let renderedHeight = 0;

      const pageCanvas = document.createElement("canvas");
      const ctx = pageCanvas.getContext("2d");

      const pageCanvasHeight = (pageHeight * canvasWidth) / pageWidth;

      while (renderedHeight < canvasHeight) {
        const remainingHeight = canvasHeight - renderedHeight;

        // Skip tiny slices (prevents blank page)
        if (remainingHeight <= 2) break;

        const sliceHeight = Math.min(remainingHeight, pageCanvasHeight);

        pageCanvas.width = canvasWidth;
        pageCanvas.height = sliceHeight;

        ctx.clearRect(0, 0, canvasWidth, sliceHeight);

        ctx.drawImage(
          canvas,
          0,
          renderedHeight,
          canvasWidth,
          sliceHeight,
          0,
          0,
          canvasWidth,
          sliceHeight,
        );

        const imgData = pageCanvas.toDataURL("image/webp", 0.5);
        const pdfHeight = (sliceHeight * pageWidth) / canvasWidth;

        if (sliceHeight > 10) {
          if (renderedHeight > 0) {
            pdf.addPage();
          }
          pdf.addImage(imgData, "WEBP", 0, 0, pageWidth, pdfHeight);
        }

        renderedHeight += sliceHeight;
      }

      pdf.save(`${fileTitle}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
    }

    setLoading(false);
  };

  return { targetRef, loading, generatePDF };
};

export default usePdf;
